'use client';

import { ElementRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import { tokens } from '@/lib/design-tokens';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = ({
  ref,
  className,
  children,
  placeholderColor,
  hideArrow,
  gap,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  gap?: string;
  placeholderColor?: string;
  hideArrow?: boolean;
} & {
  ref?: React.RefObject<ElementRef<typeof SelectPrimitive.Trigger> | null>;
}) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'focus:ring-ring/70 bg-input-bg flex h-auto w-full cursor-pointer items-center justify-between gap-2 rounded-md border-0 px-2 py-1 text-sm focus:ring-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      gap || 'gap-2',
      placeholderColor || 'data-placeholder:text-muted-foreground',
      className
    )}
    {...props}
  >
    {children}
    {!hideArrow ? (
      <SelectPrimitive.Icon asChild>
        <ChevronDown className={cn(tokens.icon(), 'opacity-50')} />
      </SelectPrimitive.Icon>
    ) : null}
  </SelectPrimitive.Trigger>
);
SelectTrigger.displayName =
  SelectPrimitive.Trigger.displayName || 'SelectTrigger';

const SelectScrollUpButton = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> & {
  ref?: React.RefObject<ElementRef<
    typeof SelectPrimitive.ScrollUpButton
  > | null>;
}) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronUp className={tokens.icon()} />
  </SelectPrimitive.ScrollUpButton>
);
SelectScrollUpButton.displayName =
  SelectPrimitive.ScrollUpButton.displayName || 'SelectScrollUpButton';

const SelectScrollDownButton = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> & {
  ref?: React.RefObject<ElementRef<
    typeof SelectPrimitive.ScrollDownButton
  > | null>;
}) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronDown className={tokens.icon()} />
  </SelectPrimitive.ScrollDownButton>
);
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName || 'SelectScrollDownButton';

const SelectContent = ({
  ref,
  className,
  children,
  position = 'popper',
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
  ref?: React.RefObject<ElementRef<typeof SelectPrimitive.Content> | null>;
}) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[70] max-h-(--radix-select-content-available-height) min-w-32 origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);
SelectContent.displayName =
  SelectPrimitive.Content.displayName || 'SelectContent';

const SelectLabel = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & {
  ref?: React.RefObject<ElementRef<typeof SelectPrimitive.Label> | null>;
}) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('py-1.5 pr-2 pl-8 text-sm font-semibold', className)}
    {...props}
  />
);
SelectLabel.displayName = SelectPrimitive.Label.displayName || 'SelectLabel';

const SelectItem = ({
  ref,
  className,
  children,
  description,
  padding,
  hideIndicator,
  indicatorPosition = 'left',
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  padding?: string;
  hideIndicator?: boolean;
  /** Optional muted subtext rendered on a second line, below the item's label.
   * Kept OUTSIDE `SelectPrimitive.ItemText` on purpose: Radix mirrors only the
   * ItemText into the collapsed trigger, so the description shows in the open
   * list but never in the trigger value. */
  description?: React.ReactNode;
  /** Side the selection checkmark renders on. Defaults to 'left' (shadcn
   * default); filter dropdowns use 'right' to match the checkmark-right
   * convention used by the app's other filter surfaces (LF-4961). */
  indicatorPosition?: 'left' | 'right';
} & {
  ref?: React.RefObject<ElementRef<typeof SelectPrimitive.Item> | null>;
}) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground relative flex w-full cursor-pointer items-center rounded-sm text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50',
      padding ||
        (hideIndicator
          ? 'px-2 py-1.5'
          : indicatorPosition === 'right'
            ? 'py-1.5 pr-8 pl-2'
            : 'py-1.5 pr-2 pl-8'),
      className
    )}
    {...props}
  >
    {!hideIndicator && (
      <span
        className={cn(
          'absolute flex items-center justify-center',
          indicatorPosition === 'right' ? 'right-2' : 'left-2',
          tokens.icon('xs')
        )}
      >
        <SelectPrimitive.ItemIndicator>
          <Check className={tokens.icon()} />
        </SelectPrimitive.ItemIndicator>
      </span>
    )}

    {description ? (
      <span className="flex min-w-0 flex-col">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        <span className="text-muted-foreground text-xs">{description}</span>
      </span>
    ) : (
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    )}
  </SelectPrimitive.Item>
);
SelectItem.displayName = SelectPrimitive.Item.displayName || 'SelectItem';

const SelectSeparator = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> & {
  ref?: React.RefObject<ElementRef<typeof SelectPrimitive.Separator> | null>;
}) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('bg-muted -mx-1 my-1 h-px', className)}
    {...props}
  />
);
SelectSeparator.displayName =
  SelectPrimitive.Separator.displayName || 'SelectSeparator';

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton
};
